import i18n from "i18next";
import { initReactI18next } from "react-i18next";

export const SUPPORTED_LANGS = ["nl", "fr", "en"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];
export const DEFAULT_LANG: Lang = "nl";

export const NAMESPACES = [
  "analytics",
  "common",
  "nav",
  "home",
  "audit",
  "contact",
  "seo",
  "pkg",
  "site",
  "pages",
  "analyse",
  "secondopinion",
  "agencies",
  "pricing",
  "rapport",
  "privacy",
  "security",
  "projects",
] as const;

type Bundle = Record<string, Record<string, unknown>>;

/**
 * Elke taal is een aparte async chunk. Alleen de taal die de bezoeker
 * daadwerkelijk bekijkt wordt geladen — de andere twee zitten niet in de
 * hoofdbundel.
 */
const LOADERS: Record<Lang, () => Promise<{ default: Bundle }>> = {
  nl: () => import("./locales/nl/index"),
  fr: () => import("./locales/fr/index"),
  en: () => import("./locales/en/index"),
};

const loaded = new Set<Lang>();
const pending = new Map<Lang, Promise<void>>();

function addBundle(lang: Lang, bundle: Bundle) {
  for (const [ns, resources] of Object.entries(bundle)) {
    i18n.addResourceBundle(lang, ns, resources, true, true);
  }
  loaded.add(lang);
}

/** Laadt (eenmalig) de resources van één taal. */
export function ensureLang(lang: Lang): Promise<void> {
  if (loaded.has(lang)) return Promise.resolve();
  let promise = pending.get(lang);
  if (!promise) {
    promise = LOADERS[lang]()
      .then((mod) => {
        addBundle(lang, mod.default);
      })
      .finally(() => {
        pending.delete(lang);
      });
    pending.set(lang, promise);
  }
  return promise;
}

/**
 * SSR schrijft de resources van de actieve taal als JSON in de HTML
 * (zie RootShell). Zo heeft de eerste client-render exact dezelfde
 * vertalingen als de server, zonder extra request en zonder de andere
 * talen mee te bundelen.
 */
function readInlineBundle(): { lang: Lang; resources: Bundle; partial: boolean } | null {
  if (typeof document === "undefined") return null;
  const el = document.getElementById("i18n-data");
  if (!el?.textContent) return null;
  try {
    const parsed = JSON.parse(el.textContent) as {
      lang?: string;
      resources?: Bundle;
      partial?: boolean;
    };
    if (!isLang(parsed.lang) || !parsed.resources) return null;
    return { lang: parsed.lang, resources: parsed.resources, partial: Boolean(parsed.partial) };
  } catch {
    return null;
  }
}

function detectInitialLang(): Lang {
  if (typeof window === "undefined") return DEFAULT_LANG;
  const first = window.location.pathname.split("/").filter(Boolean)[0];
  return isLang(first) ? first : DEFAULT_LANG;
}

if (!i18n.isInitialized) {
  const inline = readInlineBundle();
  const initialLang = inline?.lang ?? detectInitialLang();
  void i18n.use(initReactI18next).init({
    resources: inline ? ({ [inline.lang]: inline.resources } as Record<string, Bundle>) : {},
    partialBundledLanguages: true,
    lng: initialLang,
    fallbackLng: DEFAULT_LANG,
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    defaultNS: "common",
    ns: NAMESPACES as unknown as string[],
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    returnNull: false,
  });
  // Alleen wanneer de volledige taal inline stond mag de lazy chunk
  // overgeslagen worden; bij een gedeeltelijke payload wordt de rest
  // alsnog geladen zodra de bezoeker navigeert of interactie heeft.
  if (inline && !inline.partial) loaded.add(inline.lang);
}

export function isLang(v: unknown): v is Lang {
  return typeof v === "string" && (SUPPORTED_LANGS as readonly string[]).includes(v);
}

/**
 * Serialiseert de resources van één taal voor de SSR-payload. Met `only`
 * gaan alleen de namespaces mee die de huidige pagina nodig heeft; dat
 * scheelt tientallen KB's HTML en dus FCP op mobiel. De overige namespaces
 * komen later via `ensureLang`.
 */
export function serializeBundle(lang: Lang, only?: readonly string[]): string {
  const all = (i18n.getDataByLanguage(lang) ?? {}) as Bundle;
  let resources: Bundle = all;
  if (only) {
    resources = {};
    for (const entry of only) {
      const [ns, subtree] = entry.split(":");
      const source = ns ? all[ns] : undefined;
      if (!source) continue;
      if (!subtree) {
        resources[ns as string] = source;
        continue;
      }
      // Deelboom: alleen dat blok meesturen (bv. alleen home.cta).
      const current = (resources[ns as string] ??= {});
      if (subtree in source) current[subtree] = source[subtree] as never;
    }
  }
  return JSON.stringify({ lang, resources, partial: Boolean(only) });
}

/** Server- en client-veilige taalwissel; laadt de resources indien nodig. */
export async function setLang(lang: Lang) {
  await ensureLang(lang);
  if (i18n.language !== lang) await i18n.changeLanguage(lang);
}

export default i18n;
